import { ExportDataSchema } from '@portfolio-pm/shared';
import {
  type AppBadgeCountModeT,
  DEFAULT_PRIORITY_COLORS,
  type PriorityT,
  type TaskSwipeActionT,
} from '@portfolio-pm/shared';
import { useRef, useState } from 'react';
import { getDocStore } from '../../store/doc-store.ts';
import {
  type DisplayDensity,
  type VisualTheme,
  useDisplayPreferences,
} from '../display/display-preferences.ts';
import { usePushStatus } from '../notifications/usePushStatus.ts';
import { ConnectionStatusSection } from './ConnectionStatusSection.tsx';
import { convertRtmExportToPpm } from './rtm-import.ts';

export interface SettingsModalProps {
  onClose(): void;
}

/**
 * Settings modal: JSON export + import, and "Reset local data" escape hatch.
 *
 * The modal is rendered as a `<dialog>`-less overlay kept inside a single
 * component for testability (no context or portal needed at this scale).
 */
export function SettingsModal({ onClose }: SettingsModalProps): JSX.Element {
  const store = getDocStore();
  const {
    preferences: displayPreferences,
    updatePreferences: updateDisplayPreferences,
    resetPreferences: resetDisplayPreferences,
  } = useDisplayPreferences();
  const reminderPreferences = store.useReminderPreferences();
  const uiPreferences = store.useUiPreferences();
  const push = usePushStatus();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rtmFileInputRef = useRef<HTMLInputElement>(null);
  const [importStatus, setImportStatus] = useState<'idle' | 'success' | { error: string }>('idle');
  const [rtmImportStatus, setRtmImportStatus] = useState<
    'idle' | { success: string } | { error: string }
  >('idle');

  // ── Export ────────────────────────────────────────────────────────────────
  function handleExport(): void {
    const data = store.exportData();
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `portfolio-pm-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Import ────────────────────────────────────────────────────────────────
  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const raw: unknown = JSON.parse(text);
      const result = ExportDataSchema.safeParse(raw);
      if (!result.success) {
        const msg = result.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join('; ');
        setImportStatus({ error: `Validation failed — ${msg}` });
        return;
      }
      store.importData(result.data);
      setImportStatus('success');
    } catch (err) {
      setImportStatus({ error: err instanceof Error ? err.message : String(err) });
    } finally {
      // Reset file input so the same file can be re-imported after an error.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleRtmFileChange(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const raw: unknown = JSON.parse(text);
      const converted = convertRtmExportToPpm(raw);
      const result = ExportDataSchema.safeParse(converted.data);
      if (!result.success) {
        const msg = result.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join('; ');
        setRtmImportStatus({ error: `Validation failed — ${msg}` });
        return;
      }

      const resetFirst = window.confirm(
        'Reset to a fresh workspace before importing this Remember the Milk export? Choose OK to replace the current workspace with the RTM task set. Choose Cancel to merge the RTM tasks into current data.',
      );
      if (resetFirst) store.clearUserData();

      store.importData(result.data);
      setRtmImportStatus({
        success: `Remember the Milk import successful. Imported ${converted.projectCount} projects and ${converted.taskCount} tasks.`,
      });
    } catch (err) {
      setRtmImportStatus({ error: err instanceof Error ? err.message : String(err) });
    } finally {
      if (rtmFileInputRef.current) rtmFileInputRef.current.value = '';
    }
  }

  // ── Clear content ─────────────────────────────────────────────────────────
  function handleClearContent(): void {
    if (
      !window.confirm(
        'Reset to a fresh workspace? All tasks, OKRs, projects, and smart lists will be deleted. Device preferences are kept. This cannot be undone.',
      )
    ) {
      return;
    }
    store.clearUserData();
  }

  // ── Reset ─────────────────────────────────────────────────────────────────
  async function handleReset(): Promise<void> {
    if (
      !window.confirm(
        'Development reset: wipe local data and the configured dev sync room? This cannot be undone.',
      )
    ) {
      return;
    }
    await store.resetLocalData();
    window.location.reload();
  }

  return (
    // Backdrop — native <dialog> satisfies biome's useSemanticElements rule and
    // gives us keyboard/focus-trap behaviour for free.
    // The outer wrapper handles backdrop-click dismissal.
    <div
      data-testid="settings-modal"
      className="mobile-safe-overlay fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--app-modal-overlay)]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <dialog
        open
        aria-label="Settings"
        className="flex max-h-[calc(100dvh-2rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-6 text-[color:var(--app-text)] shadow-xl"
      >
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[color:var(--app-text)]">Settings</h2>
            <p
              data-testid="settings-app-version"
              className="mt-0.5 font-mono text-[10px] tracking-wide text-[color:var(--app-text-soft)]"
            >
              v{__APP_VERSION__} · {__APP_BUILD_REVISION__}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close settings"
            onClick={onClose}
            className="rounded p-1 text-[color:var(--app-text-muted)] hover:text-[color:var(--app-text)]"
          >
            ×
          </button>
        </div>

        <div className="min-h-0 overflow-auto pr-1">
          <ConnectionStatusSection />

          <section className="mb-6 rounded-xl border border-[color:var(--app-border)] bg-[color:var(--app-panel-muted)] p-4">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-medium text-[color:var(--app-text)]">Display</h3>
                <p className="mt-2 text-xs text-[color:var(--app-text-muted)]">
                  These layout preferences stay on this device and do not change synced data.
                </p>
              </div>
              <button
                type="button"
                data-testid="settings-display-reset"
                onClick={resetDisplayPreferences}
                className="ui-button"
              >
                Reset layout
              </button>
            </div>
            <fieldset className="mb-4">
              <legend className="mb-2 text-[11px] font-medium uppercase tracking-[0.18em] text-[color:var(--app-text-soft)]">
                Visual theme
              </legend>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {VISUAL_THEMES.map((theme) => (
                  <label
                    key={theme.id}
                    data-active={displayPreferences.visualTheme === theme.id || undefined}
                    className="theme-option cursor-pointer rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-2"
                  >
                    <input
                      type="radio"
                      name="visual-theme"
                      value={theme.id}
                      checked={displayPreferences.visualTheme === theme.id}
                      data-testid={`settings-theme-${theme.id}`}
                      onChange={() => updateDisplayPreferences({ visualTheme: theme.id })}
                      className="sr-only"
                    />
                    <span
                      className="mb-2 grid h-10 grid-cols-[0.55fr_1.45fr] overflow-hidden rounded-md border border-black/10"
                      aria-hidden="true"
                    >
                      <span style={{ background: theme.sidebar }} />
                      <span className="flex items-center bg-white px-2">
                        <span
                          className="h-1.5 w-full rounded-full"
                          style={{ backgroundColor: theme.accent }}
                        />
                      </span>
                    </span>
                    <span className="block text-xs font-medium text-[color:var(--app-text)]">
                      {theme.label}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <h4 className="text-sm font-medium text-[color:var(--app-text)]">View settings</h4>
            <DisplayToggle
              label="Show estimate chips in mobile task lists"
              checked={displayPreferences.showMobileTaskEstimates}
              testId="settings-mobile-task-estimates-toggle"
              onChange={(checked) => updateDisplayPreferences({ showMobileTaskEstimates: checked })}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <SettingsSelect
                label="Density"
                testId="settings-density-select"
                value={displayPreferences.density}
                options={['compact', 'standard', 'comfortable']}
                onChange={(value) => updateDisplayPreferences({ density: value as DisplayDensity })}
                formatter={formatDisplayOption}
              />
              <DisplayToggle
                label="Higher contrast"
                checked={displayPreferences.highContrast}
                testId="settings-high-contrast-toggle"
                onChange={(checked) => updateDisplayPreferences({ highContrast: checked })}
              />
              <DisplayToggle
                label="Reduced motion"
                checked={displayPreferences.reducedMotion}
                testId="settings-reduced-motion-toggle"
                onChange={(checked) =>
                  updateDisplayPreferences({
                    reducedMotion: checked,
                    reducedMotionExplicit: true,
                  })
                }
              />
              <div className="rounded border border-[color:var(--app-border)] bg-[color:var(--app-panel)] px-3 py-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-[color:var(--app-text-soft)]">
                  Pane widths
                </p>
                <p
                  data-testid="settings-pane-widths"
                  className="mt-1 text-sm text-[color:var(--app-text)]"
                >
                  Sidebar {displayPreferences.sidebarWidth}px · Detail{' '}
                  {displayPreferences.taskDetailWidth}px
                </p>
              </div>
            </div>
          </section>

          <section className="mb-6 rounded-xl border border-[color:var(--app-border)] bg-[color:var(--app-panel-muted)] p-4">
            <h3 className="mb-2 text-sm font-medium text-[color:var(--app-text)]">
              Mobile task swipes
            </h3>
            <p className="mb-4 text-xs text-[color:var(--app-text-muted)]">
              Choose the direct task action for each horizontal swipe. These preferences sync with
              the workspace.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <SettingsSelect
                label="Swipe right"
                testId="settings-task-swipe-right"
                value={uiPreferences.taskSwipeLeftToRight}
                options={['complete', 'postpone']}
                onChange={(value) =>
                  store.updateUiPreferences({
                    taskSwipeLeftToRight: value as TaskSwipeActionT,
                  })
                }
                formatter={formatTaskSwipeAction}
              />
              <SettingsSelect
                label="Swipe left"
                testId="settings-task-swipe-left"
                value={uiPreferences.taskSwipeRightToLeft}
                options={['complete', 'postpone']}
                onChange={(value) =>
                  store.updateUiPreferences({
                    taskSwipeRightToLeft: value as TaskSwipeActionT,
                  })
                }
                formatter={formatTaskSwipeAction}
              />
            </div>
          </section>

          <section className="mb-6 rounded-xl border border-[color:var(--app-border)] bg-[color:var(--app-panel-muted)] p-4">
            <h3 className="mb-2 text-sm font-medium text-[color:var(--app-text)]">Notifications</h3>
            <p className="mb-4 text-xs text-[color:var(--app-text-muted)]">
              Reminder nudges sync with your room data. Browser push permission and subscription
              stay on this device.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <ReminderToggle
                label="Due nudges"
                checked={reminderPreferences.dueNudgesEnabled}
                testId="settings-due-nudges-toggle"
                onChange={(checked) => {
                  store.updateReminderPreferences({ dueNudgesEnabled: checked });
                }}
              />
              <ReminderNumberInput
                label="Due lead minutes"
                value={reminderPreferences.dueLeadMinutes}
                disabled={!reminderPreferences.dueNudgesEnabled}
                testId="settings-due-lead-input"
                onChange={(value) => {
                  store.updateReminderPreferences({ dueLeadMinutes: value });
                }}
              />
              <ReminderToggle
                label="Scheduled nudges"
                checked={reminderPreferences.scheduledNudgesEnabled}
                testId="settings-scheduled-nudges-toggle"
                onChange={(checked) => {
                  store.updateReminderPreferences({ scheduledNudgesEnabled: checked });
                }}
              />
              <ReminderNumberInput
                label="Scheduled lead minutes"
                value={reminderPreferences.scheduledLeadMinutes}
                disabled={!reminderPreferences.scheduledNudgesEnabled}
                testId="settings-scheduled-lead-input"
                onChange={(value) => {
                  store.updateReminderPreferences({ scheduledLeadMinutes: value });
                }}
              />
              <SettingsSelect
                label="Home-screen badge"
                testId="settings-app-badge-count-mode"
                value={uiPreferences.appBadgeCountMode}
                options={['today', 'open']}
                onChange={(value) =>
                  store.updateUiPreferences({ appBadgeCountMode: value as AppBadgeCountModeT })
                }
                formatter={formatAppBadgeCountMode}
              />
            </div>
            <p className="mt-3 text-xs text-[color:var(--app-text-muted)]">
              Uses the installed-app badge where your browser supports it. The badge updates while
              Portfolio PM is open.
            </p>

            <div className="mt-4 rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-[color:var(--app-text)]">Push subscription</p>
                  <p
                    data-testid="push-status-text"
                    className="text-xs text-[color:var(--app-text-muted)]"
                  >
                    {describePushStatus(push.loading, push.status.state)}
                  </p>
                </div>
                {push.status.state === 'subscribed' ? (
                  <button
                    type="button"
                    data-testid="push-unsubscribe-button"
                    disabled={push.loading}
                    onClick={() => void push.unsubscribe()}
                    className="ui-button disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Unsubscribe
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="push-subscribe-button"
                    disabled={
                      push.loading ||
                      push.status.state === 'unsupported' ||
                      push.status.state === 'missing_config' ||
                      push.status.state === 'denied'
                    }
                    onClick={() => void push.subscribe()}
                    className="ui-button ui-button-primary disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Enable push
                  </button>
                )}
              </div>
            </div>
          </section>

          <section className="mb-6 rounded-xl border border-[color:var(--app-border)] bg-[color:var(--app-panel-muted)] p-4">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-medium text-[color:var(--app-text)]">
                  Priority colors
                </h3>
                <p className="mt-2 text-xs text-[color:var(--app-text-muted)]">
                  These row marker colors sync with the workspace and are included in exports.
                </p>
              </div>
              <button
                type="button"
                data-testid="settings-priority-colors-reset"
                onClick={() =>
                  store.updateUiPreferences({ priorityColors: DEFAULT_PRIORITY_COLORS })
                }
                className="ui-button"
              >
                Reset
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {([1, 2, 3, 0] as PriorityT[]).map((priority) => (
                <PriorityColorInput
                  key={priority}
                  priority={priority}
                  value={uiPreferences.priorityColors[priority]}
                  onChange={(color) =>
                    store.updateUiPreferences({
                      priorityColors: {
                        ...uiPreferences.priorityColors,
                        [priority]: color,
                      },
                    })
                  }
                />
              ))}
            </div>
          </section>

          {/* Export */}
          <section className="mb-6">
            <h3 className="mb-2 text-sm font-medium text-[color:var(--app-text)]">Export data</h3>
            <p className="mb-3 text-xs text-[color:var(--app-text-muted)]">
              Download all your projects and tasks as a JSON file.
            </p>
            <button
              type="button"
              data-testid="export-button"
              onClick={handleExport}
              className="ui-button"
            >
              Download JSON export
            </button>
          </section>

          {/* Import */}
          <section className="mb-6">
            <h3 className="mb-2 text-sm font-medium text-[color:var(--app-text)]">Import data</h3>
            <p className="mb-3 text-xs text-[color:var(--app-text-muted)]">
              Merge a previously exported JSON file. Existing items are preserved; newer remote
              records win on conflict.
            </p>
            <label className="ui-button inline-flex cursor-pointer">
              Choose JSON file
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                data-testid="import-file-input"
                className="sr-only"
                onChange={handleFileChange}
              />
            </label>

            {importStatus === 'success' && (
              <output data-testid="import-success" className="mt-2 block text-xs text-green-400">
                Import successful.
              </output>
            )}
            {typeof importStatus === 'object' && (
              <p role="alert" data-testid="import-error" className="mt-2 text-xs text-red-400">
                {importStatus.error}
              </p>
            )}
          </section>

          <section className="mb-6 rounded-xl border border-[color:var(--app-border)] bg-[color:var(--app-panel-muted)] p-4">
            <h3 className="mb-2 text-sm font-medium text-[color:var(--app-text)]">
              Import Remember the Milk JSON
            </h3>
            <p className="mb-3 text-xs text-[color:var(--app-text-muted)]">
              Convert an RTM JSON export, then choose whether to reset to a fresh workspace first.
              Use reset when you want this app to mirror the current RTM task set.
            </p>
            <label className="ui-button inline-flex cursor-pointer">
              Choose RTM JSON file
              <input
                ref={rtmFileInputRef}
                type="file"
                accept=".json,application/json"
                data-testid="rtm-import-file-input"
                className="sr-only"
                onChange={handleRtmFileChange}
              />
            </label>

            {typeof rtmImportStatus === 'object' && 'success' in rtmImportStatus && (
              <output
                data-testid="rtm-import-success"
                className="mt-2 block text-xs text-green-400"
              >
                {rtmImportStatus.success}
              </output>
            )}
            {typeof rtmImportStatus === 'object' && 'error' in rtmImportStatus && (
              <p role="alert" data-testid="rtm-import-error" className="mt-2 text-xs text-red-400">
                {rtmImportStatus.error}
              </p>
            )}
          </section>

          {/* Danger zone */}
          <section className="border-t border-[color:var(--app-border)] pt-5">
            <h3 className="mb-2 text-sm font-medium text-red-400">Development danger zone</h3>
            <p className="mb-3 text-xs text-[color:var(--app-text-muted)]">
              For development and testing only.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                data-testid="clear-content-button"
                onClick={handleClearContent}
                className="rounded border border-red-800 px-3 py-1.5 text-sm text-red-400 hover:border-red-600 hover:text-red-200"
              >
                Reset to fresh workspace
              </button>
              <button
                type="button"
                data-testid="reset-button"
                onClick={handleReset}
                className="rounded border border-red-800 px-3 py-1.5 text-sm text-red-400 hover:border-red-600 hover:text-red-200"
              >
                Full reset (wipes sync room)
              </button>
            </div>
          </section>
        </div>
      </dialog>
    </div>
  );
}

const VISUAL_THEMES: ReadonlyArray<{
  id: VisualTheme;
  label: string;
  sidebar: string;
  accent: string;
}> = [
  {
    id: 'cool-mist',
    label: 'Cool Mist',
    sidebar: 'linear-gradient(180deg, #dce9ee, #c8dce4)',
    accent: '#287e86',
  },
  {
    id: 'sage-daylight',
    label: 'Sage Daylight',
    sidebar: 'linear-gradient(180deg, #e4eadf, #d0ddca)',
    accent: '#47734b',
  },
  {
    id: 'ink-sky',
    label: 'Ink & Sky',
    sidebar: 'linear-gradient(180deg, #14283a, #0f2131)',
    accent: '#246bc1',
  },
  {
    id: 'lilac-slate',
    label: 'Lilac Slate',
    sidebar: 'linear-gradient(180deg, #cbc8da, #aaa8bf)',
    accent: '#6659a6',
  },
];

function PriorityColorInput({
  priority,
  value,
  onChange,
}: {
  priority: PriorityT;
  value: string;
  onChange(next: string): void;
}): JSX.Element {
  const label = priority === 0 ? 'No priority' : `P${priority}`;

  return (
    <label className="flex items-center gap-3 rounded border border-[color:var(--app-border)] bg-[color:var(--app-panel)] px-3 py-2">
      <span
        className="h-7 w-7 shrink-0 rounded-full border border-[color:var(--app-border)]"
        style={{ backgroundColor: value }}
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1 text-sm text-[color:var(--app-text)]">{label}</span>
      <input
        type="color"
        value={value}
        data-testid={`settings-priority-color-${priority}`}
        onChange={(event) => onChange(event.target.value)}
        className="h-8 w-10 cursor-pointer rounded border border-[color:var(--app-border)] bg-[color:var(--app-input-bg)] p-1"
      />
    </label>
  );
}

function describePushStatus(loading: boolean, state: string): string {
  if (loading) return 'Checking browser push status...';
  if (state === 'subscribed') return 'Push reminders are enabled on this device.';
  if (state === 'unsubscribed') return 'Push is available but not enabled on this device.';
  if (state === 'denied') return 'Browser notifications are blocked for this site.';
  if (state === 'missing_config') return 'Push is not configured for this deployment.';
  return 'This browser does not support web push for the app.';
}

function formatDisplayOption(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function DisplayToggle({
  label,
  checked,
  testId,
  onChange,
}: {
  label: string;
  checked: boolean;
  testId: string;
  onChange(next: boolean): void;
}): JSX.Element {
  return (
    <label className="flex items-center gap-3 rounded border border-[color:var(--app-border)] bg-[color:var(--app-panel)] px-3 py-2">
      <input
        type="checkbox"
        checked={checked}
        data-testid={testId}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 rounded border-[color:var(--app-border)] bg-[color:var(--app-input-bg)] text-[color:var(--app-accent)]"
      />
      <span className="text-sm text-[color:var(--app-text)]">{label}</span>
    </label>
  );
}

function ReminderToggle({
  label,
  checked,
  testId,
  onChange,
}: {
  label: string;
  checked: boolean;
  testId: string;
  onChange(next: boolean): void;
}): JSX.Element {
  return (
    <label className="flex items-center gap-3 rounded border border-[color:var(--app-border)] bg-[color:var(--app-panel)] px-3 py-2">
      <input
        type="checkbox"
        checked={checked}
        data-testid={testId}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 rounded border-[color:var(--app-border)] bg-[color:var(--app-input-bg)] text-[color:var(--app-accent)]"
      />
      <span className="text-sm text-[color:var(--app-text)]">{label}</span>
    </label>
  );
}

function ReminderNumberInput({
  label,
  value,
  disabled,
  testId,
  onChange,
}: {
  label: string;
  value: number;
  disabled: boolean;
  testId: string;
  onChange(next: number): void;
}): JSX.Element {
  return (
    <label className="space-y-1">
      <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-[color:var(--app-text-soft)]">
        {label}
      </span>
      <input
        type="number"
        min={0}
        step={1}
        value={value}
        disabled={disabled}
        data-testid={testId}
        onChange={(event) => {
          const parsed = Number(event.target.value);
          if (Number.isInteger(parsed) && parsed >= 0) onChange(parsed);
        }}
        className="ui-input py-2 disabled:cursor-not-allowed disabled:opacity-50"
      />
    </label>
  );
}

function SettingsSelect({
  label,
  testId,
  value,
  options,
  onChange,
  formatter = formatDisplayOption,
}: {
  label: string;
  testId: string;
  value: string;
  options: readonly string[];
  onChange(next: string): void;
  formatter?: (value: string) => string;
}): JSX.Element {
  return (
    <label className="space-y-1">
      <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-[color:var(--app-text-soft)]">
        {label}
      </span>
      <select
        data-testid={testId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="ui-select py-2"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {formatter(option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function formatTaskSwipeAction(value: string): string {
  return value === 'postpone' ? 'Postpone by one day' : 'Complete task';
}

function formatAppBadgeCountMode(value: string): string {
  return value === 'open' ? 'All open tasks' : 'Due or scheduled today';
}
